import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useEffect, useState } from 'react';
import { Table, message, Button, Space } from 'antd';
import {
  CREATE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
} from 'constants/alerts';
import { COLUMN_MEDIUM, PAGE_SIZE_OPTIONS, SCROLL_Y } from 'constants/table';
import { SubscribeService } from 'services/SubscribeService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import ClientsSelect from 'components/ClientsSelect';
import { AccessControl } from 'components/AccessControl';
import { useQueryParam } from 'hooks/useQueryParam';
import { checkValue, formatDate } from 'utils';
import { statusSubscribe } from './mock';

const PAGE_TITLE = 'Подписки';

function Subscribers() {
  useDocumentTitle(PAGE_TITLE);
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [loading, setLoading] = useState(true);
  const clientId = useQueryParam('client_id');
  const [subscribers, setSubscribers] = useState([]);
  const [recurentId, setRecurentId] = useState(null);

  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
  });

  const {
    page,
    totalRows,
    perPage,
    setTotalRows,
    setPagination,
    resetPagination,
  } = usePagination();

  useEffect(() => {
    fetchSubscribers();
  }, [page, perPage, filters]);

  const fetchSubscribers = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await SubscribeService.getSubscribers(
        filters,
        page,
        perPage
      );

      setSubscribers(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      width: COLUMN_MEDIUM,
      title: 'Клиент',
      dataIndex: 'company_name',
      key: 'company_name',
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (i) => statusSubscribe[i],
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Дата списания',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (date) => {
        if (date === checkValue(date)) return formatDate(date, 'DD.MM.YYYY');
        return checkValue(date);
      },
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Дата следующего списания',
      dataIndex: 'next_charge_off_date',
      key: 'next_charge_off_date',
      render: (date) => {
        if (date === checkValue(date)) return formatDate(date, 'DD.MM.YYYY');
        return checkValue(date);
      },
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Услуга',
      dataIndex: 'package_name',
      key: 'package_name',
      render: checkValue,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Опция',
      dataIndex: 'option_name',
      key: 'option_name',
      render: checkValue,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Всего / списано',
      dataIndex: 'recur_freq_used',
      key: 'recur_freq_used',
      render: (i, t) => `${t.recur_freq} / ${i}`,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Срок подписки',
      dataIndex: 'recur_exp',
      key: 'recur_exp',
      render: (date) => {
        if (date === checkValue(date)) return formatDate(date, 'DD.MM.YYYY');
        return checkValue(date);
      },
    },
    {
      width: COLUMN_MEDIUM,
      title: 'PAN код',
      dataIndex: 'pan_code',
      key: 'pan_code',
      render: checkValue,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Дата деактивации',
      dataIndex: 'cancel_date',
      key: 'cancel_date',
      render: (date) => {
        if (date === checkValue(date)) return formatDate(date, 'DD.MM.YYYY');
        return checkValue(date);
      },
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Причина деактивации',
      dataIndex: 'canceled_reason',
      key: 'canceled_reason',
      render: checkValue,
    },
  ];

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const manualRecurent = async () => {
    const hide = message.loading(REQUEST_LOADING, 199);

    try {
      const isCreated = await SubscribeService.createManualPayment(recurentId);

      if (isCreated) {
        message.success(SUCCESS_CREATE);
        fetchSubscribers();
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          clientId={clientId}
          pageTitle={PAGE_TITLE}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <AccessControl pageId="subscribers" action="create">
          <Space>
            <Button
              onClick={manualRecurent}
              disabled={!recurentId}
              type="primary"
            >
              Списать в ручную
            </Button>
          </Space>
        </AccessControl>
      </ActionsWrapper>
      <Table
        columns={columns}
        dataSource={subscribers}
        loading={loading}
        rowSelection={{
          type: 'radio',
          onChange: (id, row) => {
            if (row[0].status === 'LIMIT_ATTEMPT_EXCEEDED') setRecurentId(id);
          },
        }}
        scroll={{ y: SCROLL_Y }}
        pagination={{
          current: page,
          total: totalRows,
          pageSize: perPage,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
        rowKey="id"
        onChange={setPagination}
      />
    </>
  );
}

export { Subscribers };
